import { toDollars } from '@/util/currency';
import { calculateForecastAmount } from '@common/forecasting';
import {
    GeneralInvestingGoal,
    getAnnualReturnUsingRiskScore,
} from '@common/goals';
import { CurrentRateInfo } from '@common/graphql/generated';
import { getAmountRecurrenceStr, getGoalBaseConfig } from '@common/react/goals';
import { UserInfo } from '@common/react/user';
import { dt } from '@common/util/dateTime';
import { isNumber } from '@common/util/types';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getFinalAssumptionsWithRisk,
    getGoalForecastStartAmount,
    getInNumYears,
    getIOrWe,
    getTargetDateFromStartDateAndYears,
    horizonByEarliestWithdrawal,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

export const GENERAL_INVESTING: GoalConfig<GeneralInvestingGoal> = {
    inputSteps: [],
    getGoalStatementContent: ({
        goalData,
        userInfo,
        onGoalStatementClick,
        isReadOnlyIfTargetDateIsDynamic,
        currentRateInfo,
    }) => {
        const { projectedAmount, inNumYearsStr } = getProjectedAmountData(
            goalData,
            userInfo,
            currentRateInfo
        );

        if (
            !goalData.transactionSchedulePlan ||
            !goalData.earliestWithdrawal ||
            !isNumber(projectedAmount) ||
            !inNumYearsStr
        ) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = GENERAL_INVESTING.color;

        const hasBalance = isNumber(goalData.balance) && goalData.balance > 0;
        const hasRecurrence =
            goalData.transactionSchedulePlan &&
            goalData.transactionSchedulePlan.amount > 0;
        const projectedAmountStr = `${iOrWe}'ll have an estimated ${toDollars(
            projectedAmount
        )} ${inNumYearsStr}.`;

        // TODO: Revise this to consider both partners

        // If user has existing balance and no recurring transactions,
        // just report the projected amount.
        if (hasBalance && !hasRecurrence) {
            return <>{projectedAmountStr}</>;
        }

        // Otherwise, show the full statement, omitting irrelevant parts as
        // necessary.
        return (
            <>
                {`If ${iOrWe} `}
                {/* If user has existing balance, omit the initial deposit phrase */}
                {!hasBalance && goalData.initialDepositPlan ? (
                    <>
                        {'start with '}
                        <GoalStatementLinkText
                            text={toDollars(goalData.initialDepositPlan)}
                            onClick={() =>
                                onGoalStatementClick('initialDepositPlan')
                            }
                            color={color}
                            isReadOnly={isReadOnlyIfTargetDateIsDynamic}
                        />
                    </>
                ) : null}
                {!hasBalance && !!goalData.initialDepositPlan && hasRecurrence
                    ? ' and '
                    : null}
                {hasRecurrence ? (
                    <>
                        {'invest '}
                        <GoalStatementLinkText
                            text={getAmountRecurrenceStr(
                                goalData.transactionSchedulePlan
                            )}
                            onClick={() =>
                                onGoalStatementClick('transactionSchedulePlan')
                            }
                            color={color}
                            isReadOnly={isReadOnlyIfTargetDateIsDynamic}
                        />
                    </>
                ) : null}
                {`, ${iOrWe}'ll have a hypothetical ${toDollars(
                    projectedAmount
                )} ${inNumYearsStr}.`}
            </>
        );
    },
    getAssumptionsContent: ({ goalData, userInfo, currentRateInfo }) =>
        getFinalAssumptionsWithRisk({
            goalData,
            recommendedRiskScore: GENERAL_INVESTING.getRecommendedRiskScore({
                goalData,
                userInfo,
            }),
            currentRateInfo,
        }),
    getRecommendedRiskScore: ({ goalData, userInfo }) => {
        if (
            !userInfo.recommendedRiskScoreByHorizon ||
            !goalData.earliestWithdrawal
        ) {
            return null;
        }

        return userInfo.recommendedRiskScoreByHorizon[
            horizonByEarliestWithdrawal[goalData.earliestWithdrawal]
        ];
    },
    getTargetDate: () => dt().add(25, 'years').toDateStr(),
    ...getGoalBaseConfig('GENERAL_INVESTING'),
};

function getProjectedAmountData(
    goalData: GoalData<GeneralInvestingGoal>,
    userInfo: UserInfo,
    currentRateInfo: CurrentRateInfo
): {
    projectedAmount?: number;
    inNumYears?: number;
    inNumYearsStr?: string;
} {
    // FIXME: This is broken for shared goals.
    const {
        earliestWithdrawal,
        riskScore,
        initialDepositPlan,
        transactionSchedulePlan,
        balance,
    } = goalData;

    const { inNumYears, inNumYearsStr } = earliestWithdrawal
        ? getInNumYears({ age: userInfo.age, earliestWithdrawal })
        : { inNumYears: undefined, inNumYearsStr: undefined };

    const startDate = dt().toDateStr(); // TODO: Reconsider this assumption
    const targetDate = getTargetDateFromStartDateAndYears(
        startDate,
        inNumYears
    );
    const recommendedRiskScore = GENERAL_INVESTING.getRecommendedRiskScore({
        goalData,
        userInfo,
    });
    const annualReturn = getAnnualReturnUsingRiskScore({
        investmentType:
            (riskScore ?? recommendedRiskScore) === 0
                ? 'HIGH_YIELD_CASH'
                : goalData.investmentType,
        riskScore: riskScore ?? recommendedRiskScore,
        currentRateInfo,
    });

    const projectedAmount =
        transactionSchedulePlan && targetDate
            ? calculateForecastAmount({
                  transactionSchedulePlans: transactionSchedulePlan
                      ? [transactionSchedulePlan]
                      : undefined,
                  annualGrowthRate: annualReturn,
                  startAmount: getGoalForecastStartAmount(
                      balance,
                      initialDepositPlan
                  ),
                  startDate: dt().toDateStr(),
                  targetDate,
              })
            : undefined;

    return {
        projectedAmount,
        inNumYears,
        inNumYearsStr,
    };
}
